/**
 * guild_rules.ts — GUILDS: territory, the Guild Master and the council, versona trade guilds (2026-09-24).
 * BYTE-IDENTICAL client ≡ server (server/src/lib/guild_rules.ts; verify:guilds diffs the two). No imports.
 *
 *   TERRITORY  A guild holds a DISC of land around its hall — its own small system on the map, always smaller than a
 *              hamlet's plateau (115 m). A player guild's claim grows by TIER (members + a treasury payment) and is
 *              kept by a weekly upkeep; a lapse shrinks it one tier (Life is Feudal's monument). A versona guild's
 *              claim grows with its membership. Claims never overlap each other, a settlement's plateau or a player
 *              city; inside a claim only the guild's members may build.
 *   THE COURT  A Guild Master and a council (the livery companies' Master and Court of Assistants): both elected by
 *              the members for a TERM (SWG checked its guild leader's votes every week). The master by plurality; the
 *              council by APPROVAL — each member approves up to as many candidates as there are seats and the most
 *              approved win (the direct multi-winner extension of approval voting). The council decides MOTIONS by
 *              majority of the court (council + master).
 *   VERSONA GUILDS  When ten townsfolk share a trade (Dwarf Fortress's threshold) the town charters a trade guild at
 *              one of its baked seats outside the walls; the members elect their own master and council.
 */

// ─── Territory ──────────────────────────────────────────────────────────────────────────────────────────
/** A hamlet's plateau radius (terrain.ts PLATEAU_RADIUS.hamlet): every guild claim is smaller. */
export const HAMLET_PLATEAU_M = 115;
/** A player guild's claim radius by tier (m). The hall itself is ~95 × 87 m, so tier 1 starts at 80. */
export const GUILD_TERRITORY_RADIUS: readonly number[] = [0, 80, 90, 100, 110];
export const GUILD_MAX_TIER = 4;
/** Members (players + the versonas the guild employs) a tier needs. */
export const GUILD_TIER_MIN_MEMBERS: readonly number[] = [0, 1, 5, 10, 20];
/** SOV from the treasury to raise the claim TO a tier (tier 1 comes with the charter). */
export const GUILD_TIER_COST: readonly number[] = [0, 0, 2000, 5000, 10000];
/** Weekly upkeep per tier (SOV, from the treasury). Unpaid, the claim shrinks one tier (never below 1). */
export const GUILD_UPKEEP_PER_TIER = 150;
export const GUILD_UPKEEP_PERIOD_S = 7 * 86_400;
/** A versona guild's claim radius by tier, and the members each tier needs. */
export const VERSONA_GUILD_RADIUS: readonly number[] = [0, 45, 55, 65];
export const VERSONA_GUILD_TIER_MIN: readonly number[] = [0, 10, 16, 24];
export const VERSONA_GUILD_MAX_TIER = 3;
/** Two claims (or a claim and a plateau / a city) keep this much ground between them. */
export const TERRITORY_GAP_M = 10;

export function playerGuildRadius(tier: number): number {
  const t = Math.max(1, Math.min(GUILD_MAX_TIER, Math.floor(tier)));
  return GUILD_TERRITORY_RADIUS[t];
}
export function versonaGuildRadius(tier: number): number {
  const t = Math.max(1, Math.min(VERSONA_GUILD_MAX_TIER, Math.floor(tier)));
  return VERSONA_GUILD_RADIUS[t];
}
/** The highest tier a membership may hold (the treasury still pays to climb). */
export function playerTierCap(members: number): number {
  let t = 1;
  for (let k = 2; k <= GUILD_MAX_TIER; k++) if (members >= GUILD_TIER_MIN_MEMBERS[k]) t = k;
  return t;
}
export function versonaGuildTier(members: number): number {
  let t = 1;
  for (let k = 2; k <= VERSONA_GUILD_MAX_TIER; k++) if (members >= VERSONA_GUILD_TIER_MIN[k]) t = k;
  return t;
}

export interface Disc { x: number; z: number; r: number }
/** Do two discs come within `gap` of each other? */
export function discsMeet(a: Disc, b: Disc, gap = TERRITORY_GAP_M): boolean {
  const dx = a.x - b.x, dz = a.z - b.z, rr = a.r + b.r + gap;
  return dx * dx + dz * dz < rr * rr;
}
export function pointInDisc(d: Disc, x: number, z: number): boolean {
  const dx = x - d.x, dz = z - d.z;
  return dx * dx + dz * dz <= d.r * d.r;
}

/** An oriented box in THREE's frame (local x → (cos, −sin), local z → (sin, cos)) — the structure frame. */
export interface GObb { x: number; z: number; hx: number; hz: number; yaw: number }
export function obbCorners(o: GObb): Array<[number, number]> {
  const c = Math.cos(o.yaw), s = Math.sin(o.yaw);
  const out: Array<[number, number]> = [];
  for (const [sx, sz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as Array<[number, number]>) {
    const lx = sx * o.hx, lz = sz * o.hz;
    out.push([o.x + c * lx + s * lz, o.z - s * lx + c * lz]);
  }
  return out;
}
/** The whole box inside the disc (all four corners). */
export function obbInsideDisc(o: GObb, d: Disc): boolean {
  for (const [x, z] of obbCorners(o)) if (!pointInDisc(d, x, z)) return false;
  return true;
}
/** Does the box touch the disc at all? (The disc centre's distance to the box, in the box's own frame.) */
export function obbTouchesDisc(o: GObb, d: Disc): boolean {
  const c = Math.cos(o.yaw), s = Math.sin(o.yaw);
  const dx = d.x - o.x, dz = d.z - o.z;
  // world → local: the transpose of (x = c lx + s lz, z = −s lx + c lz)
  const lx = c * dx - s * dz, lz = s * dx + c * dz;
  const qx = Math.max(Math.abs(lx) - o.hx, 0), qz = Math.max(Math.abs(lz) - o.hz, 0);
  return qx * qx + qz * qz <= d.r * d.r;
}
/** Metres from a point to a box's edge (0 inside). A ground war's seat is its BUILDING: "within 20 m of the seat"
 *  measured from the centre was inside a 95 × 87 m hall (2026-09-24). */
export function distanceToBox(px: number, pz: number, o: GObb): number {
  const dx = px - o.x, dz = pz - o.z;
  const c = Math.cos(o.yaw), s = Math.sin(o.yaw);
  const lx = dx * c - dz * s, lz = dx * s + dz * c;
  return Math.hypot(Math.max(0, Math.abs(lx) - o.hx), Math.max(0, Math.abs(lz) - o.hz));
}

/** The guild hall's footprint (the model ×50, `STRUCTURE_FOOTPRINT.guild_hall`) and a versona hall's (the same model ×20). */
export const PLAYER_HALL_FOOTPRINT = { hx: 47.4, hz: 43.3, h: 79.2 } as const;
/** A hired guild worker's post, hall-local (the caller turns it by the hall's yaw), OUTSIDE the walls: guards and
 *  greeters before the door (the −Z face), the trades around the sides, the manager behind. The offsets used to be
 *  fixed metres sized for a ~30 m hall — at the hall's real 95 × 87 m every post stood inside it (2026-09-24). */
export function guildWorkerPost(job: string, gvId: bigint, fp: { hx: number; hz: number } = PLAYER_HALL_FOOTPRINT): { dx: number; dz: number; state: string } {
  const hash = (salt: string): number => {
    let h = 0x811c9dc5;
    const k = `${gvId}|${salt}`;
    for (let i = 0; i < k.length; i++) { h ^= k.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13;
    return (h >>> 0) / 4294967296;
  };
  const j = hash('post');
  const W = fp.hx, D = fp.hz;
  switch (job) {
    case 'guild_guard': return { dx: (hash('g') < 0.5 ? -1 : 1) * (6 + j * 3), dz: -(D + 5 + j * 4), state: 'patrolling' };
    case 'greeter': return { dx: (j - 0.5) * 4, dz: -(D + 3 + j * 2), state: 'idle' };
    case 'farmer': return { dx: W + 6 + j * 6, dz: 6 + j * 8, state: 'working' };
    case 'miner': return { dx: -(W + 6 + j * 6), dz: 10 + j * 6, state: 'working' };
    case 'crafter': return { dx: W + 4 + j * 4, dz: -8 - j * 4, state: 'working' };
    case 'weapon_builder': return { dx: -(W + 4 + j * 4), dz: -8 - j * 4, state: 'working' };
    case 'droid_builder': return { dx: -(W + 8 + j * 4), dz: 2 + j * 4, state: 'working' };
    case 'guild_manager': return { dx: (j - 0.5) * 6, dz: D + 4 + j * 4, state: 'working' };
    case 'guild_diplomat': return { dx: 4, dz: -(D + 6), state: 'idle' };
    default: return { dx: (j - 0.5) * 10, dz: D + 8 + j * 6, state: 'idle' };
  }
}
export const VERSONA_HALL_SCALE = 20;
export const VERSONA_HALL_FOOTPRINT = { hx: 19.0, hz: 17.3, h: 31.7 } as const;

// ─── Guild structures (placed from a member's deed as guild property, inside the claim) ───────────────────
/** The deed types a guild may raise on its land, and the tier each needs. The houses are guild quarters. */
export const GUILD_STRUCTURE_MIN_TIER: Record<string, number> = {
  garden: 1, statue: 1, fountain: 1, street_lamp: 1, turret: 1, city_wall: 1, outpost: 1, civic_decoration: 1,
  small_house: 1, store: 2, market: 2, bunker: 2, garage: 2, medium_house: 2, cantina: 3, hospital: 3, museum: 3,
  pool: 3, large_house: 3, factory: 4, mining_facility: 4, hotel: 4,
};
export function isGuildStructureType(type: string): boolean { return type in GUILD_STRUCTURE_MIN_TIER; }
/** Guild property is kept by the treasury: its maintenance pool is topped up to this many hours of upkeep. */
export const GUILD_STRUCTURE_POOL_HOURS = 48;

// ─── The court: the Guild Master and the council ────────────────────────────────────────────────────────
/** The member-row title the Guild Master holds (kept 'leader' — every older reader keys on it). */
export const MASTER_TITLE = 'leader';
export const COUNCIL_TITLE = 'council';
/** The pre-council offices read as council seats (wardens and an exchequer sat on the court). */
export function isCouncilTitle(title: string): boolean { return title === COUNCIL_TITLE || title === 'warden' || title === 'exchequer'; }
/** How many council seats a membership supports. */
export function councilSeats(members: number): number {
  if (members <= 4) return 2;
  if (members <= 9) return 3;
  if (members <= 19) return 5;
  return 7;
}
export const GUILD_OFFICES = ['leader', 'council'] as const;
export const GUILD_TERM_S = 7 * 86_400;
export const GUILD_ELECTION_S = 24 * 3_600;
export const GUILD_MOTION_S = 24 * 3_600;

/**
 * THE APPROVAL TALLY: each voter's approvals count once per candidate; the most approved win, ties to the earlier
 * candidacy, then the id. Only candidates with at least one approval are seated.
 */
export function approvalTally(
  ballots: ReadonlyArray<{ voter: string; candidate: string }>,
  candidates: ReadonlyArray<{ id: string; at: number }>,
  seats: number,
): string[] {
  const listed = new Map<string, number>();
  for (const c of candidates) listed.set(c.id, c.at);
  const seen = new Set<string>();
  const n = new Map<string, number>();
  for (const b of ballots) {
    if (!listed.has(b.candidate)) continue;
    const k = `${b.voter}|${b.candidate}`;
    if (seen.has(k)) continue;
    seen.add(k);
    n.set(b.candidate, (n.get(b.candidate) ?? 0) + 1);
  }
  return [...n.entries()]
    .sort((a, b) => b[1] - a[1] || (listed.get(a[0])! - listed.get(b[0])!) || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .slice(0, Math.max(0, seats))
    .map(([id]) => id);
}
/** Plurality with the same tie rule (the master's election). */
export function pluralityWinner(
  ballots: ReadonlyArray<{ voter: string; candidate: string }>,
  candidates: ReadonlyArray<{ id: string; at: number }>,
): string | null {
  const last = new Map<string, string>();
  for (const b of ballots) last.set(b.voter, b.candidate); // one ballot a voter — the latest stands
  return approvalTally([...last.entries()].map(([voter, candidate]) => ({ voter, candidate })), candidates, 1)[0] ?? null;
}

// ─── Motions ────────────────────────────────────────────────────────────────────────────────────────────
/** spend: pay a member from the treasury · expand: raise the claim a tier · policy: 0 open door, 1 by application
 *  · expel: remove a member · recall: an early election for Guild Master. */
export const MOTION_KINDS = ['spend', 'expand', 'policy', 'expel', 'recall'] as const;
export type MotionKind = (typeof MOTION_KINDS)[number];
/** A motion passes once MORE than half the court says yes; it fails once half says no (it can no longer pass). */
export function motionVerdict(yes: number, no: number, court: number): 'passed' | 'failed' | 'open' {
  if (court <= 0) return 'failed';
  if (yes * 2 > court) return 'passed';
  if (no * 2 >= court) return 'failed';
  return 'open';
}

// ─── The treasury ───────────────────────────────────────────────────────────────────────────────────────
/** A guild's treasury is a ledger wallet: Identity(GUILD_VAULT_BASE + guildId) (the banks are 0xba4c…, the firms 0x5f1a…). */
export const GUILD_VAULT_BASE = 0x6a11_0000_0000n;

// ─── Versona trade guilds ───────────────────────────────────────────────────────────────────────────────
/** Townsfolk of one trade a guild needs to be chartered (Dwarf Fortress: ten of a profession). */
export const VERSONA_GUILD_FOUND_MIN = 10;
export const VERSONA_GUILD_MEMBER_CAP = 30;
/** How many guild seats each tier of settlement has (the bake lays at most this many). */
export const VERSONA_GUILD_SEATS: Record<string, number> = { capital: 4, city: 2, town: 1, hamlet: 0, district: 0, circuit: 0 };
/** Every trade's guild: its members' noun, a three-letter tag, and a banner hue (0..360). */
export const TRADE_GUILDS: Record<string, { noun: string; tag: string; hue: number }> = {
  farmer: { noun: 'Tillers', tag: 'TIL', hue: 95 },
  miner: { noun: 'Delvers', tag: 'DLV', hue: 30 },
  smith: { noun: 'Smiths', tag: 'SMI', hue: 12 },
  merchant: { noun: 'Merchant Adventurers', tag: 'MER', hue: 48 },
  guard: { noun: 'Watch', tag: 'WAT', hue: 220 },
  bard: { noun: 'Minstrels', tag: 'MIN', hue: 290 },
  scout: { noun: 'Wayfarers', tag: 'WAY', hue: 160 },
  fisher: { noun: 'Fishmongers', tag: 'FSH', hue: 195 },
  forester: { noun: 'Woodwards', tag: 'WOD', hue: 120 },
  hunter: { noun: 'Huntsmen', tag: 'HNT', hue: 70 },
  herbalist: { noun: 'Apothecaries', tag: 'APO', hue: 140 },
  tapper: { noun: 'Tappers', tag: 'TAP', hue: 330 },
  gas_tender: { noun: 'Gaswrights', tag: 'GAS', hue: 260 },
};
export function versonaGuildName(trade: string, town: string): string {
  const t = TRADE_GUILDS[trade];
  return `The ${t ? t.noun : 'Guild'} of ${town || 'the Commons'}`;
}
/** A versona guild's seat on the map: [x, z, yaw] (yaw: three's rotation.y; the door on local −Z faces the town). */
export type GuildSeat = [number, number, number];

/** FNV-1a + the murmur3 finalizer → [0, 1). */
export function gh01(a: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < a.length; i++) { h ^= a.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
/**
 * How much a versona member wants a candidate at the head of the guild: craft first (the best hand leads the
 * trade), then seniority in the guild, then the voter's own leaning (a stable hash of the pair — friendships and
 * grudges the sim does not model per pair). Deterministic.
 */
export function versonaVoteUtility(voter: string, candidate: string, skill: number, seniorityDays: number): number {
  return skill * 10 + Math.min(30, Math.max(0, seniorityDays)) * 0.2 + gh01(`${voter}>${candidate}`) * 4;
}
/** Each versona approves this many candidates for the council (their favourites). */
export const VERSONA_APPROVALS = 3;
/** Once per MEETING epoch a guild's members gather at their hall (only watched meetings are staged). */
export const VERSONA_GUILD_MEETING_S = 6 * 3_600;
export const VERSONA_GUILD_MEETING_LEN_S = 240;
/** The dues a member pays into the guild's purse each market day (coins), and the relief it pays a hungry member. */
export const VERSONA_GUILD_DUES = 1n;
export const VERSONA_GUILD_RELIEF = 3n;
